import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, SparklesIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import GameRoomFormModal from '../components/gameRooms/GameRoomFormModal';
import gameRoomService, { GameRoom } from '../api/services/gameRoom.service';

const GameManagement: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  /* =========================================================
     GAME ROOMS QUERY
     - 5m staleTime
     - refetchOnMount: false → cached rooms render instantly
     - refetchOnWindowFocus: false → no refetch on tab switch
  ========================================================= */
  const {
    data: rooms,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['game-rooms'],
    queryFn: () => gameRoomService.getAll(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['game-rooms'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const renderStatusBadge = (room: GameRoom) => {
    if (!room.isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
          Disabled
        </span>
      );
    }
    if (room.status === 'running') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          Live
        </span>
      );
    }
    if (room.status === 'waiting') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700">
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
          Waiting
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
        Finished
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent mx-auto" />
          <p className="mt-2 text-sm text-gray-500">Loading rooms...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500">
            {error instanceof Error ? error.message : 'Failed to load rooms'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const roomsList = rooms ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Game Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage your game rooms and their configurations.
          </p>
        </div>
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-1.5" />
          Add Room
        </Button>
      </div>

      {roomsList.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <PuzzlePieceIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No game rooms yet</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Click "Add Room" to create your first one.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roomsList.map((room) => (
            <button
              key={room._id}
              onClick={() => navigate(`/game-rooms/${room._id}`)}
              className="text-left group"
            >
              <Card className="h-full transition-all duration-200 group-hover:shadow-md group-hover:border-primary-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {room.isSuperBingo ? (
                      <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                        <SparklesIcon className="w-4 h-4 text-primary-600" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                        <PuzzlePieceIcon className="w-4 h-4 text-secondary-600" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {room.roomId}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {room.isSuperBingo ? 'Super Bingo' : 'Standard'}
                      </p>
                    </div>
                  </div>
                  {renderStatusBadge(room)}
                </div>

                <p className="text-xs text-gray-500 line-clamp-2 mb-3 min-h-[32px]">
                  {room.description || '—'}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-[10px] text-gray-400">Stake</p>
                    <p className="text-sm font-semibold text-gray-900">
                      ETB {room.betAmount.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-400">Players</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {room.playersCount}
                    </p>
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}

      <GameRoomFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={handleSuccess}
        room={null}
      />
    </div>
  );
};

export default GameManagement;